import React, { useState } from 'react';
import { Card, Form, InputGroup, Button, Spinner, ButtonGroup, Modal } from 'react-bootstrap';
import RequestViewer from './RequestViewer';
import {
  KeyRound, FileKey, Shield, Eye, EyeOff, CheckCircle2,
  Upload, ChevronDown, Lock, ShieldCheck, CloudUpload, Trash2, AlertTriangle,
} from 'lucide-react';

const CredentialForm = ({
  authMode, setAuthMode,
  rfc, setRfc,
  rfcManualMode, setRfcManualMode,
  rfcManualInput, setRfcManualInput,
  rfcOptions, rfcLoading,
  privateKeyFile, setPrivateKeyFile,
  certFile, setCertFile,
  fielPassword, setFielPassword,
  secret, setSecret,
  signingKeyStatus, signingKeyLoading,
  saveToVault, setSaveToVault,
  onRemoveSigningKey,
  lastVaultRequest,
}) => {
  const [showFielPassword, setShowFielPassword] = useState(false);
  const [showSecret, setShowSecret]             = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [removing, setRemoving]                   = useState(false);
  const [removeError, setRemoveError]             = useState('');

  const handleRfcSelectChange = (e) => setRfc(e.target.value);

  const handleManualRfcChange = (e) => {
    const val = e.target.value.toUpperCase().replace(/[^A-Z0-9&Ñ]/g, '');
    setRfcManualInput(val);
    setRfc(val);
  };

  const truncateFileName = (name) => {
    if (!name || name.length <= 28) return name;
    return name.substring(0, 12) + '…' + name.substring(name.length - 8);
  };

  const rfcSelector = (
    <div>
      {!rfcManualMode ? (
        <>
          {rfcLoading ? (
            <div className="d-flex align-items-center gap-2 mt-1">
              <Spinner animation="border" size="sm" />
              <span className="text-muted small">Cargando RFCs…</span>
            </div>
          ) : (
            <Form.Select className="form-select-modern" value={rfc} onChange={handleRfcSelectChange}>
              <option value="">— Seleccionar RFC —</option>
              {rfcOptions.map((o) => (
                <option key={o.rfc} value={o.rfc}>
                  {o.rfc}{o.alias ? ` — ${o.alias}` : ''}
                </option>
              ))}
            </Form.Select>
          )}
          <Button variant="link" size="sm" className="p-0 mt-1 text-muted" style={{ fontSize: '.8rem' }}
            onClick={() => { setRfcManualMode(true); setRfc(''); }}>
            Ingresar RFC manualmente
          </Button>
        </>
      ) : (
        <>
          <Form.Control className="form-control-modern" type="text"
            placeholder="Ej. XAXX010101000" maxLength={13}
            value={rfcManualInput} onChange={handleManualRfcChange} />
          <Button variant="link" size="sm" className="p-0 mt-1 text-muted" style={{ fontSize: '.8rem' }}
            onClick={() => { setRfcManualMode(false); setRfcManualInput(''); setRfc(''); }}>
            <ChevronDown size={12} className="me-1" />
            Seleccionar de mis RFC
          </Button>
        </>
      )}
    </div>
  );

  const vaultRegistered = signingKeyStatus?.isRegistered === true;

  const handleRemove = async () => {
    setRemoving(true);
    setRemoveError('');
    const result = await onRemoveSigningKey();
    setRemoving(false);
    if (result.success) {
      setShowRemoveConfirm(false);
    } else {
      setRemoveError(result.error || 'No se pudo eliminar la FIEL del vault.');
    }
  };

  return (
    <>
    <Card className="fiel-credentials-card border-0 shadow-sm mb-4">
      <Card.Header className="d-flex align-items-center justify-content-between">
        <span className="d-flex align-items-center gap-2">
          <Shield size={18} className="text-primary" />
          Credenciales de autenticación
        </span>

        <ButtonGroup size="sm">
          <Button
            variant={authMode === 'fiel' ? 'primary' : 'outline-primary'}
            onClick={() => setAuthMode('fiel')}
            style={{ borderRadius: '8px 0 0 8px', fontWeight: 600, minWidth: 64 }}
          >
            FIEL
          </Button>
          <Button
            variant={authMode === 'ciec' ? 'primary' : 'outline-primary'}
            onClick={() => setAuthMode('ciec')}
            style={{ borderRadius: '0 8px 8px 0', fontWeight: 600, minWidth: 64 }}
          >
            CIEC
          </Button>
        </ButtonGroup>
      </Card.Header>

      <Card.Body className="p-4">
        {authMode === 'fiel' ? (
          <div className="row g-3">

            {/* RFC */}
            <div className="col-12 col-md-4">
              <Form.Label className="form-label-modern">
                <KeyRound size={14} className="me-1" />RFC
              </Form.Label>
              {rfcSelector}
              {/* Spinner mientras se consulta el estado del vault */}
              {rfc && signingKeyLoading && (
                <div className="d-flex align-items-center gap-1 mt-1">
                  <Spinner animation="border" size="sm" style={{ width: 12, height: 12 }} />
                  <span className="text-muted" style={{ fontSize: '.75rem' }}>
                    Verificando Key Vault…
                  </span>
                </div>
              )}
            </div>

            {vaultRegistered ? (
              /* ── FIEL ya guardada en Key Vault ─────────────────────── */
              <>
                {/* Badge de estado */}
                <div className="col-12 col-md-8 d-flex align-items-end">
                  <div className="d-flex align-items-center gap-2 p-2 rounded w-100"
                    style={{ background: 'rgba(25,135,84,.07)', border: '1px solid rgba(25,135,84,.25)' }}>
                    <ShieldCheck size={18} className="text-success flex-shrink-0" />
                    <div className="flex-grow-1">
                      <div className="fw-semibold text-success" style={{ fontSize: '.85rem' }}>
                        FIEL guardada en Key Vault
                      </div>
                      <div className="text-muted" style={{ fontSize: '.75rem' }}>
                        La llave privada está almacenada en SatGo — no necesitas subirla.
                        {signingKeyStatus?.keyName && (
                          <> Certificado: <code style={{ fontSize: '.72rem' }}>{signingKeyStatus.keyName}</code></>
                        )}
                      </div>
                    </div>
                    <Button variant="outline-danger" size="sm"
                      onClick={() => { setRemoveError(''); setShowRemoveConfirm(true); }}
                      className="d-flex align-items-center gap-1 flex-shrink-0"
                      style={{ fontSize: '.78rem' }}>
                      <Trash2 size={13} />
                      Eliminar
                    </Button>
                  </div>
                </div>

              </>
            ) : (
              /* ── Formulario FIEL completo ──────────────────────────── */
              <>
                {/* Llave privada (.key) */}
                <div className="col-12 col-md-4">
                  <Form.Label className="form-label-modern">
                    <FileKey size={14} className="me-1" />Llave privada (.key)
                  </Form.Label>
                  <input type="file" accept=".key" className="credential-file-input"
                    id="fiel-key-file"
                    onChange={(e) => { const f = e.target.files[0]; if (f) setPrivateKeyFile(f); }} />
                  <label htmlFor="fiel-key-file"
                    className={`credential-file-label w-100 ${privateKeyFile ? 'file-selected' : ''}`}>
                    {privateKeyFile ? (
                      <><CheckCircle2 size={16} className="credential-status-icon text-success" />
                        <span className="text-truncate">{truncateFileName(privateKeyFile.name)}</span></>
                    ) : (
                      <><Upload size={16} className="credential-status-icon" />
                        <span>Seleccionar archivo .key</span></>
                    )}
                  </label>
                </div>

                {/* Certificado (.cer) */}
                <div className="col-12 col-md-4">
                  <Form.Label className="form-label-modern">
                    <FileKey size={14} className="me-1" />Certificado (.cer)
                  </Form.Label>
                  <input type="file" accept=".cer" className="credential-file-input"
                    id="fiel-cert-file"
                    onChange={(e) => { const f = e.target.files[0]; if (f) setCertFile(f); }} />
                  <label htmlFor="fiel-cert-file"
                    className={`credential-file-label w-100 ${certFile ? 'file-selected' : ''}`}>
                    {certFile ? (
                      <><CheckCircle2 size={16} className="credential-status-icon text-success" />
                        <span className="text-truncate">{truncateFileName(certFile.name)}</span></>
                    ) : (
                      <><Upload size={16} className="credential-status-icon" />
                        <span>Seleccionar archivo .cer</span></>
                    )}
                  </label>
                </div>

                {/* Contraseña FIEL */}
                <div className="col-12 col-md-5">
                  <Form.Label className="form-label-modern">
                    <KeyRound size={14} className="me-1" />Contraseña FIEL
                  </Form.Label>
                  <InputGroup>
                    <Form.Control className="form-control-modern"
                      type={showFielPassword ? 'text' : 'password'}
                      placeholder="Contraseña del certificado"
                      value={fielPassword}
                      onChange={(e) => setFielPassword(e.target.value)}
                      style={{ borderRight: 'none' }} />
                    <InputGroup.Text onClick={() => setShowFielPassword(p => !p)}
                      style={{
                        cursor: 'pointer', borderRadius: '0 10px 10px 0',
                        border: '2px solid #e9ecef', borderLeft: 'none', background: 'white',
                      }}>
                      {showFielPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </InputGroup.Text>
                  </InputGroup>
                </div>

                {/* Checkbox: guardar en Key Vault */}
                {rfc && (
                  <div className="col-12">
                    <div className="d-flex align-items-center gap-2 p-2 rounded"
                      style={{
                        background: saveToVault ? 'rgba(13,110,253,.06)' : 'rgba(108,117,125,.04)',
                        border: `1px solid ${saveToVault ? 'rgba(13,110,253,.25)' : 'rgba(108,117,125,.15)'}`,
                        transition: 'all .2s',
                      }}>
                      <Form.Check
                        type="checkbox"
                        id="save-to-vault"
                        checked={saveToVault}
                        onChange={(e) => setSaveToVault(e.target.checked)}
                        className="mb-0"
                      />
                      <label htmlFor="save-to-vault" style={{ cursor: 'pointer', marginBottom: 0 }}>
                        <div className="d-flex align-items-center gap-1">
                          <CloudUpload size={14} className={saveToVault ? 'text-primary' : 'text-muted'} />
                          <span className={`fw-semibold ${saveToVault ? 'text-primary' : 'text-muted'}`}
                            style={{ fontSize: '.85rem' }}>
                            Guardar FIEL en Key Vault
                          </span>
                        </div>
                        <div className="text-muted" style={{ fontSize: '.75rem' }}>
                          Si la consulta es exitosa, SatGo guardará tu FIEL para que no tengas
                          que volver a subirla.
                        </div>
                      </label>
                    </div>
                  </div>
                )}
              </>
            )}

          </div>
        ) : (
          /* ═══════════ CIEC ═══════════ */
          <div className="row g-3">

            <div className="col-12 col-md-4">
              <Form.Label className="form-label-modern">
                <KeyRound size={14} className="me-1" />RFC
              </Form.Label>
              {rfcSelector}
            </div>

            <div className="col-12 col-md-4">
              <Form.Label className="form-label-modern">
                <Lock size={14} className="me-1" />Secret (contraseña CIEC)
              </Form.Label>
              <InputGroup>
                <Form.Control className="form-control-modern"
                  type={showSecret ? 'text' : 'password'}
                  placeholder="Contraseña CIEC del SAT"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  style={{ borderRight: 'none' }} />
                <InputGroup.Text onClick={() => setShowSecret(p => !p)}
                  style={{
                    cursor: 'pointer', borderRadius: '0 10px 10px 0',
                    border: '2px solid #e9ecef', borderLeft: 'none', background: 'white',
                  }}>
                  {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                </InputGroup.Text>
              </InputGroup>
              <Form.Text className="text-muted" style={{ fontSize: '.78rem' }}>
                Se envía como header <code>Secret</code> en cada petición.
              </Form.Text>
            </div>

          </div>
        )}
      </Card.Body>
    </Card>

      <RequestViewer request={lastVaultRequest} />

      {/* Modal de confirmación para eliminar FIEL del vault */}
      <Modal show={showRemoveConfirm} onHide={() => setShowRemoveConfirm(false)} centered size="sm">
        <Modal.Header closeButton style={{ borderBottom: '3px solid #dc3545' }}>
          <Modal.Title className="d-flex align-items-center gap-2" style={{ fontSize: '1rem', color: '#1a1f3a' }}>
            <AlertTriangle size={18} color="#dc3545" />
            Eliminar FIEL del vault
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2" style={{ color: '#4a5568', fontSize: '.9rem' }}>
            Se eliminará la llave privada almacenada para <strong>{rfc}</strong>.
            Las próximas consultas requerirán subir los archivos FIEL nuevamente.
          </p>
          {removeError && (
            <div className="text-danger mt-2" style={{ fontSize: '.82rem' }}>{removeError}</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowRemoveConfirm(false)}
            disabled={removing}>
            Cancelar
          </Button>
          <Button variant="danger" size="sm" onClick={handleRemove}
            disabled={removing}
            className="d-flex align-items-center gap-1">
            {removing
              ? <><Spinner animation="border" size="sm" />Eliminando…</>
              : <><Trash2 size={14} />Eliminar</>
            }
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CredentialForm;
